// Bulk menu CSV import - pure parsing + assembly helpers (no DOM, no Supabase).
//
// The import is a long/tall format: one row per variant option, repeating the
// item name. A simple item is a single row with the variant columns left blank.
// Rows are grouped by name into the app's menu-item shape:
//   { itemName, price, category, variants: [{ optionLabel, options: [{ optionName, optionPrice }] }] }
//
// Canonical columns: name, price, category, variant_group, option, option_price.
// The actual headers are fuzzy-matched (see ALIASES) and can be remapped in the UI.

// Length caps mirror what the single-item add form realistically accepts, so a
// pasted spreadsheet can't smuggle in a 10k-char "name".
const CAP = { name: 80, category: 40, group: 40, option: 60 };

const FIELDS = ["name", "price", "category", "variant_group", "option", "option_price"];

// Normalize a header for matching: lowercase, trim, collapse separators to spaces.
const normHeader = (h) =>
  String(h ?? "")
    .replace(/^﻿/, "")
    .toLowerCase()
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Header aliases per canonical field. Exact canonical names win first, so
// "category" never gets stolen by the "group" alias of variant_group.
const ALIASES = {
  name: ["name", "item", "item name", "title", "product", "product name"],
  price: ["price", "cost", "amount", "base price", "unit price"],
  category: ["category", "cat", "type", "section", "menu category"],
  variant_group: ["variant group", "variantgroup", "option group", "group", "group name", "modifier", "modifier group", "variant"],
  option: ["option", "option name", "choice", "value", "variant option"],
  option_price: ["option price", "optionprice", "variant price", "add on", "addon", "extra", "extra price"],
};

const cap = (s, n) => String(s ?? "").trim().slice(0, n);

// Money cell -> non-negative number. Tolerates "$1,234.50", "P 90", blanks.
const money = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

// ── CSV text -> rows of string cells ─────────────────────────────────────────
// Quoted-field aware: handles commas/newlines inside quotes and "" escapes.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = String(text ?? "").replace(/^﻿/, "");

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\r") {
      // swallow - the \n handles the row break
    } else if (c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else {
      field += c;
    }
  }
  // trailing field/row (file not ending in a newline)
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// ── Header row -> { field: columnIndex | -1 } ────────────────────────────────
export function detectColumns(headers) {
  const norm = headers.map(normHeader);
  const mapping = {};
  for (const field of FIELDS) {
    // exact canonical match first
    let idx = norm.indexOf(field.replace(/_/g, " "));
    if (idx === -1) {
      // alias match, skipping columns already claimed by another field
      const taken = new Set(Object.values(mapping).filter((i) => i >= 0));
      idx = norm.findIndex((h, i) => !taken.has(i) && ALIASES[field].includes(h));
    }
    mapping[field] = idx;
  }
  return mapping;
}

const cellAt = (row, idx) => (idx >= 0 && idx < row.length ? row[idx] : "");
const isBlankRow = (row) => row.every((c) => String(c ?? "").trim() === "");

// ── Rows + mapping -> assembled items + summary ──────────────────────────────
// existingNames / existingCategories: lowercased Sets from current catalogue.
export function buildItems(rows, mapping, { existingNames, existingCategories } = {}) {
  const names = existingNames ?? new Set();
  const cats = existingCategories ?? new Set();

  const order = []; // preserve first-seen order
  const byKey = new Map(); // lowercased name -> item
  const priceEntries = new Map(); // lowercased name -> every non-blank price cell seen
  let skippedEmptyRows = 0;
  let invalidVariantRows = 0;
  let lastKey = null;

  for (const row of rows) {
    if (isBlankRow(row)) { skippedEmptyRows++; continue; }

    let rawName = cap(cellAt(row, mapping.name), CAP.name);
    let key = rawName.toLowerCase();

    // Blank name on a row = a continuation of the item above it.
    if (!key && lastKey) { key = lastKey; rawName = byKey.get(key).itemName; }
    if (!key) { invalidVariantRows++; continue; } // orphan row, nothing to attach to

    // Track every price the sheet actually entered for this name (blank cells
    // don't count - those are normal continuation rows for a variant option).
    const priceCell = cellAt(row, mapping.price);
    if (mapping.price >= 0 && String(priceCell ?? "").trim() !== "") {
      if (!priceEntries.has(key)) priceEntries.set(key, []);
      priceEntries.get(key).push(money(priceCell));
    }

    let item = byKey.get(key);
    if (!item) {
      item = { itemName: rawName, price: money(cellAt(row, mapping.price)), category: cap(cellAt(row, mapping.category), CAP.category), variants: [] };
      byKey.set(key, item);
      order.push(item);
    } else {
      // fill base fields from the first row that carries them
      if (!item.category) item.category = cap(cellAt(row, mapping.category), CAP.category);
      if (!item.price) item.price = money(cellAt(row, mapping.price));
    }
    lastKey = key;

    // Variant part of this row (optional).
    const groupName = cap(cellAt(row, mapping.variant_group), CAP.group);
    const optionName = cap(cellAt(row, mapping.option), CAP.option);
    if (!groupName && !optionName) continue; // simple-item row, done
    if (!groupName || !optionName) { invalidVariantRows++; continue; } // half a variant

    let group = item.variants.find((g) => g.optionLabel.toLowerCase() === groupName.toLowerCase());
    if (!group) { group = { optionLabel: groupName, options: [] }; item.variants.push(group); }
    group.options.push({ optionName, optionPrice: money(cellAt(row, mapping.option_price)) });
  }

  // Classify each assembled item.
  const newCategories = new Set();
  let priceConflicts = 0;
  const items = order.map((it) => {
    const duplicate = names.has(it.itemName.toLowerCase());
    const categoryNorm = it.category.toLowerCase();
    const newCategory = Boolean(categoryNorm) && !cats.has(categoryNorm);
    if (newCategory && !duplicate) newCategories.add(categoryNorm);

    // Same name entered on multiple rows with different prices - the first
    // one wins as the item's price, but the sheet author should know that.
    const enteredPrices = priceEntries.get(it.itemName.toLowerCase()) ?? [];
    const priceConflict = new Set(enteredPrices).size > 1;
    if (priceConflict && !duplicate) priceConflicts++;

    return {
      ...it,
      hasVariants: it.variants.length > 0,
      duplicate,
      newCategory,
      priceConflict,
      priceConflictCount: enteredPrices.length,
    };
  });

  const willImport = items.filter((i) => !i.duplicate).length;
  return {
    items,
    summary: {
      total: items.length,
      willImport,
      duplicates: items.length - willImport,
      newCategories: [...newCategories],
      skippedEmptyRows,
      invalidVariantRows,
      priceConflicts,
    },
  };
}

// ── Downloadable template (header + worked examples) ─────────────────────────
export const TEMPLATE_CSV = [
  "name,price,category,variant_group,option,option_price",
  "Espresso,3.50,Coffee,,,",
  "Latte,,Coffee,Size,Small,4",
  "Latte,,Coffee,Size,Medium,5",
  "Latte,,Coffee,Size,Large,6",
  "Latte,,Coffee,Milk,Whole,0",
  "Latte,,Coffee,Milk,Oat,0.75",
  "Croissant,4.00,Pastry,,,",
  "",
].join("\n");

export function downloadTemplate(filename = "pointbunny-menu-template.csv") {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export { FIELDS };
